// ============================================================================
// BehaviorTypeBreakdown.tsx
// Section component for displaying behavior type distribution
// ============================================================================

import { Bar } from "react-chartjs-2";
import { Fragment, useEffect, useState } from "react";
import { Container, Child, Header, LayerContent } from "../styles";
import { ChartContainer, NoData } from "../../style";
import { useAnalyticsWrapper } from "../../../../../store/store";
import {
  barOptions,
  handlePointClicked,
} from "../formUtils";
import SectionModal from "./BehaviorAnalysisSectionModal";
import {
  handleCSVDownload,
  handlePDFDownload,
} from "../../../../../utils/helpers/pdfCsvUtils";
import { behaviorAnalysisDetailCols } from "../../dummyData/colums";
import { Grid, GridColumn, GridColumnProps } from "@progress/kendo-react-grid";
import {
  CenteredWholeNumCell,
  HeaderCenteredCell,
} from "../../../../../components/DynamicGrid/customCells";
import { TotalRow } from "../../../../../components/DynamicGrid/customRows";
import useFormDataHelpers from "../../../../../hooks/useFormDataHelpers";
import { MITIGATE_COLORS } from "../../../../../shared/sharedConstants";

// ============================================================================
// Props Interface
// ============================================================================
interface BehaviorTypeBreakdownProps {
  setClickedIncident: (item: any) => void;
  isFetchingSelectedIncident: boolean;
  shareTableData?: (data: any[]) => void;
}

// ============================================================================
// Row Type Definition - behaviorType, count, and percentage
// ============================================================================
type BehaviorTypeRow = {
  behaviorType: string;
  count: number;
  percentage: number;
};

// ============================================================================
// API Response Types
// ============================================================================
interface BehaviorTypeDataItem {
  behaviorType: string;
  incidentCount: number;
  percentage: number;
}

type BehaviorTypeDrilldown = Record<string, any[]>;

// ============================================================================
// Constants - All expected behavior types for the chart/table
// ============================================================================
const ALL_BEHAVIOR_TYPES = [
  "Emotional Abuse",
  "Misappropriation",
  "Neglect",
  "Physical",
  "Resident to Resident altercation",
  "Sexual",
  "Smoking incident",
  "Suspected/actual alcohol/illicit drug use",
  "Verbal",
  "Other"
];

// ============================================================================
// Component
// ============================================================================
const BehaviorTypeBreakdown = ({
  setClickedIncident,
  isFetchingSelectedIncident,
  shareTableData,
}: BehaviorTypeBreakdownProps) => {
  // ---------------------------------------------------------------------------
  // Helper Hooks
  // ---------------------------------------------------------------------------
  const { getFacilityLabel } = useFormDataHelpers();

  // ---------------------------------------------------------------------------
  // Local State
  // ---------------------------------------------------------------------------
  const [tableData, setTableData] = useState<BehaviorTypeRow[]>([]);
  const [grid, setGrid] = useState<any>([]);
  const [barData, setBarData] = useState<any>([]);
  const [modalData, setModalData] = useState<any>([]);
  const [behaviorType, setBehaviorType] = useState("");
  const [showTypeModal, setShowTypeModal] = useState(false);

  // ---------------------------------------------------------------------------
  // Global State from Zustand
  // ---------------------------------------------------------------------------
  const { startDate, endDate, behaviorFacility, behaviorAnalysisIncidents } =
    useAnalyticsWrapper((state) => ({
      startDate: state.startDate,
      endDate: state.endDate,
      behaviorFacility: state.behaviorFacility,
      behaviorAnalysisIncidents: state.behaviorAnalysisIncidents,
    }));

  // Derived value for facility name
  const facilityLabel = getFacilityLabel(behaviorFacility);

  // ---------------------------------------------------------------------------
  // Data Transformation Function - uses behaviorTypeBreakdown from API
  // ---------------------------------------------------------------------------
  const adjustedTableData = (
    typeData: BehaviorTypeDataItem[]
  ): BehaviorTypeRow[] => {
    const returnData: BehaviorTypeRow[] = [];

    // Create maps of behavior type counts and percentages from API data
    const typeCountMap: Record<string, number> = {};
    const typePercentageMap: Record<string, number> = {};
    
    if (typeData?.length) {
      typeData.forEach((item) => {
        typeCountMap[item.behaviorType] = item.incidentCount || 0;
        typePercentageMap[item.behaviorType] = item.percentage || 0;
      });
    }

    let totalIncidents = 0;

    // Use all predefined behavior types in order
    ALL_BEHAVIOR_TYPES.forEach((type) => {
      const count = typeCountMap[type] || 0;
      const percentage = typePercentageMap[type] || 0;
      returnData.push({
        behaviorType: type,
        count: count,
        percentage: percentage,
      });
      totalIncidents += count;
    });

    // Add totals row
    returnData.push({
      behaviorType: "Types Total",
      count: totalIncidents,
      percentage: totalIncidents > 0 ? 100 : 0,
    });

    return returnData;
  };

  // ---------------------------------------------------------------------------
  // Transform table data for bar chart (ensures chart order matches table order)
  // ---------------------------------------------------------------------------
  const transformTableDataForChart = (rows: BehaviorTypeRow[]) => {
    // Filter out the totals row for the chart
    const dataRows = rows.filter(row => row.behaviorType !== "Types Total");
    
    if (dataRows.length === 0) {
      return { labels: [], datasets: [] };
    }

    const labels = dataRows.map(row => row.behaviorType);
    const data = dataRows.map(row => row.count);

    // Alternating colors from MITIGATE_COLORS
    const alternatingColors = [
      MITIGATE_COLORS.BLUE[0],
      MITIGATE_COLORS.GREEN[0],
      MITIGATE_COLORS.RED[0],
      MITIGATE_COLORS.ORANGE[0],
      MITIGATE_COLORS.PURPLE[0],
      MITIGATE_COLORS.BLUE[1],
      MITIGATE_COLORS.GREEN[1],
      MITIGATE_COLORS.RED[1],
      MITIGATE_COLORS.ORANGE[1],
      MITIGATE_COLORS.PURPLE[1],
      MITIGATE_COLORS.BLUE[2],
      MITIGATE_COLORS.GREEN[2],
      MITIGATE_COLORS.RED[2],
      MITIGATE_COLORS.ORANGE[2],
      MITIGATE_COLORS.PURPLE[2],
    ];

    return {
      labels,
      datasets: [
        {
          label: "# of Incidents",
          data,
          backgroundColor: alternatingColors.slice(0, labels.length),
          borderWidth: 0,
        },
      ],
    };
  };

  // ---------------------------------------------------------------------------
  // Process data when behaviorAnalysisIncidents changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (behaviorAnalysisIncidents !== undefined) {
      const behaviorTypeSection = behaviorAnalysisIncidents?.behaviorTypeSection;
      const breakdownData = behaviorTypeSection?.behaviorTypeBreakdown;

      const transformedData = adjustedTableData(breakdownData);
      const transformedBarData = transformTableDataForChart(transformedData);

      setTableData(transformedData);
      setBarData(transformedBarData);
      shareTableData?.(transformedData);
    }
  }, [behaviorAnalysisIncidents]);

  const title = "Behavior Type Breakdown";

  // ---------------------------------------------------------------------------
  // Handle clicking a bar in the chart
  // ---------------------------------------------------------------------------
  const handleBarClicked = (chart: any) => {
    if (!chart.length) return;
    const label = handlePointClicked(chart, barData?.labels);
    setShowTypeModal(true);
    setBehaviorType(label);
    // Get drilldown data for the clicked behavior type from behaviorTypeSection
    const drilldownData = behaviorAnalysisIncidents?.behaviorTypeSection?.behaviorTypeDrilldown || {};
    const filteredDetails = drilldownData[label] || [];
    setModalData(filteredDetails);
  };

  // ---------------------------------------------------------------------------
  // Build Kendo Grid when tableData changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const cellCountProps = {
      cells: { data: CenteredWholeNumCell, headerCell: HeaderCenteredCell },
    };
    const cellPercentageProps = {
      cells: { data: CenteredWholeNumCell, append: '%', headerCell: HeaderCenteredCell },
    };

    const grid = (
      <Fragment>
        <Grid
          data={tableData || []}
          scrollable="none"
          rows={{ data: TotalRow }}
        >
          <GridColumn field="behaviorType" title="Behavior Type" />
          <GridColumn
            field="count"
            title="# of Incidents"
            {...(cellCountProps as GridColumnProps)}
          />
          <GridColumn
            field="percentage"
            title="%"
            {...(cellPercentageProps as GridColumnProps)}
          />
        </Grid>
      </Fragment>
    );
    setGrid(grid);
  }, [tableData]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <LayerContent>
      <Header>{title}</Header>
      <Container>
        <Child flex="40%">
          {grid}
        </Child>
        <Child flex="60%">
          <ChartContainer>
            {barData?.labels?.length > 0 ? (
              <Bar options={barOptions(handleBarClicked)} data={barData} />
            ) : (
              <NoData>No Data</NoData>
            )}
          </ChartContainer>
        </Child>
      </Container>

      {showTypeModal && (
        <SectionModal
          open={showTypeModal}
          facilityName={facilityLabel}
          startDate={startDate}
          endDate={endDate}
          closeFn={() => setShowTypeModal(false)}
          resetFn={() => {}}
          isLoading={false}
          tableData={modalData}
          tableCols={behaviorAnalysisDetailCols()}
          title={`${title} - ${behaviorType}`}
          getUserCallback={(item: any) => setClickedIncident(item)}
          handlePdfExport={() =>
            handlePDFDownload({
              data: modalData,
              columnData: behaviorAnalysisDetailCols(),
              fileName: facilityLabel,
              startDate,
              endDate,
              subTitle: `${title} - ${behaviorType}`,
            })
          }
          handleCsvExport={() =>
            handleCSVDownload(
              modalData,
              behaviorAnalysisDetailCols(),
              `${facilityLabel} - ${title} - ${behaviorType}`
            )
          }
        />
      )}
    </LayerContent>
  );
};

export default BehaviorTypeBreakdown;
